// ShieldSettings.jsx — Admin control panel for the lecture video protection layer.
// Modes: single rectangle, vertical split (two zones + vertical guide line),
// horizontal split (two zones + horizontal guide line).
// Saved to D1 via /settings/player_shield and applied live by the player.
import React, { useState, useEffect, useRef } from 'react';
import { Shield, Save, RotateCcw, Play, Pause, Square, Columns2, Rows2 } from 'lucide-react';
import * as d1Api from '../../services/d1Api';
import { clampPct, getGuardZones, clampZonesToSafeStrip, safeStripPct, invertRectToStrips, safeBottomPct, menuZoneRect, punchMenuHole, mirrorX } from '../../utils/shieldZones';

const DEFAULTS = {
  enabled: true, x: 0, y: 0, w: 50, h: 55,
  opacity: 30, allowToggle: true,
  mode: 'single', split: 50, sizeA: 100, sizeB: 50, yA: 0, yB: 0, xA: 0, xB: 0,
  graceEnabled: true, graceSeconds: 10,
  graceX: 0, graceY: 0, graceW: 40, graceH: 30,
  showTimer: true, brandSize: 14, brandW: 100, brandH: 34,
  brandFsW: 100, brandFsH: 48, brandFsSize: 18,
  menuX: 58, menuW: 42, menuH: 55, menuY: null,
  fsBtnRight: 4, fsBtnBottom: 4, fsBtnW: 40, fsBtnH: 40,
  rtlMirror: false,
};

export const ShieldSettings = () => {
  const [cfg, setCfg] = useState(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: '', text: '' });

  useEffect(() => {
    (async () => {
      try {
        const res = await d1Api.getSetting('player_shield');
        if (res?.success && res.data && typeof res.data === 'object') {
          setCfg({ ...DEFAULTS, ...res.data });
        }
      } catch {
        /* keep defaults */
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => {
    setCfg((c) => ({ ...c, [k]: v }));
    setMsg({ type: '', text: '' });
  };

  const handleSave = async () => {
    setSaving(true);
    setMsg({ type: '', text: '' });
    const payload = {
      enabled: !!cfg.enabled,
      x: clampPct(cfg.x),
      y: clampPct(cfg.y),
      w: clampPct(cfg.w),
      h: clampPct(cfg.h),
      opacity: clampPct(cfg.opacity),
      allowToggle: !!cfg.allowToggle,
      mode: ['single', 'vsplit', 'hsplit'].includes(cfg.mode) ? cfg.mode : 'single',
      split: clampPct(cfg.split),
      sizeA: clampPct(cfg.sizeA),
      sizeB: clampPct(cfg.sizeB),
      yA: clampPct(cfg.yA ?? 0),
      yB: clampPct(cfg.yB ?? 0),
      xA: clampPct(cfg.xA ?? 0),
      xB: clampPct(cfg.xB ?? 0),
      graceEnabled: cfg.graceEnabled !== false,
      graceSeconds: Math.min(60, Math.max(3, Number(cfg.graceSeconds) || 10)),
      graceX: clampPct(cfg.graceX),
      graceY: clampPct(cfg.graceY),
      graceW: clampPct(cfg.graceW ?? 40),
      graceH: clampPct(cfg.graceH ?? 30),
      showTimer: cfg.showTimer !== false,
      brandSize: Math.min(26, Math.max(10, Number(cfg.brandSize) || 14)),
      brandW: Math.min(100, Math.max(20, Number(cfg.brandW) || 100)),
      brandH: Math.min(64, Math.max(24, Number(cfg.brandH) || 34)),
      brandFsW: Math.min(100, Math.max(20, Number(cfg.brandFsW) || 100)),
      brandFsH: Math.min(96, Math.max(24, Number(cfg.brandFsH) || 48)),
      brandFsSize: Math.min(30, Math.max(10, Number(cfg.brandFsSize) || 18)),
      menuX: clampPct(cfg.menuX ?? 58),
      menuW: clampPct(cfg.menuW ?? 42),
      menuH: clampPct(cfg.menuH ?? 55),
      menuY: (cfg.menuY === null || cfg.menuY === undefined || cfg.menuY === '') ? null : clampPct(cfg.menuY),
      fsBtnRight: Math.min(120, Math.max(0, Number(cfg.fsBtnRight ?? 4))),
      fsBtnBottom: Math.min(120, Math.max(0, Number(cfg.fsBtnBottom ?? 4))),
      fsBtnW: Math.min(80, Math.max(24, Number(cfg.fsBtnW) || 40)),
      fsBtnH: Math.min(80, Math.max(24, Number(cfg.fsBtnH) || 40)),
      rtlMirror: !!cfg.rtlMirror,
    };
    try {
      const res = await d1Api.saveSetting('player_shield', payload);
      if (res?.success) {
        setCfg(payload);
        setMsg({ type: 'ok', text: 'تم حفظ الإعدادات — ستطبق على الطلاب فوراً.' });
      } else {
        setMsg({ type: 'err', text: res?.error || 'فشل الحفظ، حاول مرة أخرى.' });
      }
    } catch (e) {
      setMsg({ type: 'err', text: 'فشل الحفظ، حاول مرة أخرى.' });
    } finally {
      setSaving(false);
    }
  };

  const sliderRow = (label, key, hint) => (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="text-sm font-bold text-slate-300">{label}</label>
        <span className="text-sm font-black text-brand-gold" dir="ltr">{cfg[key]}%</span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={cfg[key]}
        onChange={(e) => set(key, Number(e.target.value))}
        className="w-full accent-red-500"
        dir="ltr"
      />
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
    </div>
  );

  const alpha = clampPct(cfg.opacity) / 100;
  // Identical math to the student player: zones clipped above the reserved
  // control strip, so the preview shows exactly what the student gets.
  const { zones, guide } = getGuardZones(cfg);
  const previewRef = useRef(null);
  const [previewH, setPreviewH] = useState(0);
  useEffect(() => {
    const el = previewRef.current;
    if (!el) return;
    const update = () => setPreviewH(el.clientHeight || 0);
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [loading]);
  // Preview uses the normal-mode brand height so the safe strip matches
  // what the student player reserves (brand bar + ~48px control bar).
  const previewStripPx = Math.min(64, Math.max(24, Number(cfg.brandH) || 34)) + 48;
  const safeZones = clampZonesToSafeStrip(zones, previewH, previewStripPx);
  const stripPct = safeStripPct(previewH, previewStripPx);
  // Same always-open menu hole as the player: punched out of every overlay.
  const menuHole = menuZoneRect(stripPct, cfg);
  const openZones = punchMenuHole(safeZones, stripPct, cfg);
  // Grace-mode view: ONLY the grace rect stays open, the four strips around
  // it are blocked — identical math to the student player (mirrored too).
  const graceRaw = { x: clampPct(cfg.graceX), y: clampPct(cfg.graceY), w: clampPct(cfg.graceW ?? 40), h: clampPct(cfg.graceH ?? 30) };
  const graceView = invertRectToStrips(
    cfg.rtlMirror ? mirrorX(graceRaw) : graceRaw,
    safeBottomPct(previewH, previewStripPx)
  );
  const openGraceStrips = punchMenuHole(graceView.strips, stripPct, cfg);
  const [previewGrace, setPreviewGrace] = useState(false);

  const modes = [
    { id: 'single', label: 'مستطيل واحد', Icon: Square },
    { id: 'vsplit', label: 'تقسيم رأسي (خط بالطول)', Icon: Columns2 },
    { id: 'hsplit', label: 'تقسيم أفقي (خط بالعرض)', Icon: Rows2 },
  ];

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
        <Shield size={24} />
        حماية الفيديو
      </h2>

      {loading ? (
        <p className="text-slate-400 text-center py-8">جاري تحميل الإعدادات...</p>
      ) : (
        <>
          {/* ── Live preview ── */}
          <div className="glass-panel p-6 border-2 border-brand-red/30 bg-gradient-to-b from-brand-red/10 to-transparent">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-bold text-lg">معاينة حيّة (نفس ما سيراه الطالب)</h3>
              <button
                type="button"
                onClick={() => setPreviewGrace((v) => !v)}
                className={`px-4 py-2 rounded-xl text-sm font-bold border transition ${
                  previewGrace
                    ? 'bg-cyan-500 border-cyan-400 text-black shadow-lg'
                    : 'border-white/20 text-slate-300 hover:bg-white/10'
                }`}
              >
                {previewGrace ? '⏳ معاينة وضع المهلة (مفعّلة)' : 'معاينة وضع المهلة'}
              </button>
            </div>
            <div ref={previewRef} className="relative w-full bg-black rounded-2xl overflow-hidden" style={{ aspectRatio: '16 / 9' }}>
              {/* fake video surface */}
              <div
                className="absolute inset-0"
                style={{ background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 60%, #312e81 100%)' }}
              />
              {!previewGrace && (
              <>
              {/* guarded zone(s) — menu hole punched out, exactly like the player */}
              {cfg.enabled && openZones.map((z, i) => (
                <div
                  key={i}
                  className="absolute"
                  style={{
                    left: `${z.x}%`,
                    top: `${z.y}%`,
                    width: `${z.w}%`,
                    height: `${z.h}%`,
                    backgroundColor: `rgba(0,0,0,${alpha})`,
                    border: '2px dashed rgba(255,255,255,0.7)',
                  }}
                >
                  {i === 0 && (
                    <span className="absolute top-1 right-1 text-[11px] font-bold bg-black/60 text-white px-2 py-0.5 rounded">
                      منطقة الحماية {cfg.allowToggle ? '(تشغيل/إيقاف مسموح)' : '(بدون تشغيل/إيقاف)'}
                    </span>
                  )}
                </div>
              ))}
              {/* guide divider — visual only, no effect on clicks */}
              {cfg.enabled && guide && (
                <div
                  className="absolute"
                  style={guide.vertical
                    ? { left: `${guide.at}%`, top: 0, bottom: 0, borderLeft: '2px dashed #fbbf24' }
                    : { top: `${guide.at}%`, left: 0, right: 0, borderTop: '2px dashed #fbbf24' }}
                />
              )}
              {/* grace bounds marker — the rect that will stay open during grace */}
              {cfg.enabled && cfg.graceEnabled !== false && (
                <div
                  className="absolute"
                  style={{
                    left: `${clampPct(cfg.graceX)}%`,
                    top: `${clampPct(cfg.graceY)}%`,
                    width: `${clampPct(cfg.graceW ?? 40)}%`,
                    height: `${clampPct(cfg.graceH ?? 30)}%`,
                    border: '2px dashed rgba(34,211,238,0.9)',
                    backgroundColor: 'rgba(34,211,238,0.08)',
                  }}
                >
                  <span className="absolute bottom-1 right-1 text-[11px] font-bold bg-black/60 text-cyan-300 px-2 py-0.5 rounded">
                    المسموح أثناء المهلة
                  </span>
                </div>
              )}
              </>
              )}
              {previewGrace && cfg.enabled && (
              <>
              {/* grace mode: blocked strips (menu hole punched out) */}
              {openGraceStrips.map((z, i) => (
                <div
                  key={`gs${i}`}
                  className="absolute"
                  style={{
                    left: `${z.x}%`,
                    top: `${z.y}%`,
                    width: `${z.w}%`,
                    height: `${z.h}%`,
                    backgroundColor: `rgba(0,0,0,${alpha})`,
                    border: '1px dashed rgba(255,255,255,0.35)',
                  }}
                />
              ))}
              {/* the open rect */}
              <div
                className="absolute"
                style={{
                  left: `${graceView.hole.x}%`,
                  top: `${graceView.hole.y}%`,
                  width: `${graceView.hole.w}%`,
                  height: `${graceView.hole.h}%`,
                  border: '2px dashed rgba(34,211,238,0.9)',
                }}
              >
                <span className="absolute top-1 right-1 text-[11px] font-bold bg-black/60 text-cyan-300 px-2 py-0.5 rounded">
                  مفتوح للتحكم أثناء المهلة — باقي الفيديو محجوب
                </span>
              </div>
              </>
              )}
              {/* always-open settings-menu hole (bottom-right, above control bar) */}
              <div
                className="absolute"
                style={{
                  left: `${menuHole.x}%`,
                  top: `${menuHole.y}%`,
                  width: `${menuHole.w}%`,
                  height: `${menuHole.h}%`,
                  border: '2px dashed rgba(192,132,252,0.9)',
                  backgroundColor: 'rgba(192,132,252,0.07)',
                }}
              >
                <span className="absolute top-1 right-1 text-[11px] font-bold bg-black/60 text-purple-300 px-2 py-0.5 rounded">
                  قائمة الإعدادات — مفتوحة دائماً
                </span>
              </div>
              {/* reserved control strip — guards can never enter it */}
              {stripPct > 0 && (
                <div
                  className="absolute bottom-0 left-0 right-0 bg-green-500/15 border-t-2 border-green-400/70 flex items-start justify-center pt-1"
                  style={{ height: `${stripPct}%` }}
                >
                  <span className="text-[11px] font-bold text-green-300 bg-black/60 px-2 py-0.5 rounded">
                    شريط التحكم — مفتوح دائماً (الإعدادات والجودة والتكبير)
                  </span>
                </div>
              )}
              {/* mock grace badge (shown to the student during the grace period) */}
              {cfg.enabled && cfg.graceEnabled !== false && (
                <div className="absolute top-2 left-2 flex items-center gap-1 bg-black/75 text-white text-[11px] font-bold px-2.5 py-1 rounded-full border border-white/30">
                  <span>⏳</span>
                  <span>الجودة: {Math.min(60, Math.max(3, Number(cfg.graceSeconds) || 10))}</span>
                </div>
              )}
              {/* mock fullscreen pill at the admin-configured spot */}
              <div
                className="absolute flex items-center justify-center text-white bg-black/85 border border-white/25 rounded-[10px]"
                style={cfg.rtlMirror
                  ? {
                      left: `${Math.min(120, Math.max(0, Number(cfg.fsBtnRight ?? 4)))}px`,
                      bottom: `${Math.min(120, Math.max(0, Number(cfg.fsBtnBottom ?? 4)))}px`,
                      width: `${Math.min(80, Math.max(24, Number(cfg.fsBtnW) || 40))}px`,
                      height: `${Math.min(80, Math.max(24, Number(cfg.fsBtnH) || 40))}px`,
                      fontSize: '15px',
                    }
                  : {
                      right: `${Math.min(120, Math.max(0, Number(cfg.fsBtnRight ?? 4)))}px`,
                      bottom: `${Math.min(120, Math.max(0, Number(cfg.fsBtnBottom ?? 4)))}px`,
                      width: `${Math.min(80, Math.max(24, Number(cfg.fsBtnW) || 40))}px`,
                      height: `${Math.min(80, Math.max(24, Number(cfg.fsBtnH) || 40))}px`,
                      fontSize: '15px',
                    }}
              >
                ⛶
              </div>
              {/* mock control bar */}
              <div className="absolute bottom-0 left-0 right-0 h-9 bg-black/70 flex items-center gap-3 px-4">
                <span className="text-white text-xs">▶</span>
                <span className="text-white text-xs">🔊</span>
                <span className="flex-1 h-1 bg-white/20 rounded" />
                <span className="text-white text-xs">⚙️</span>
                <span className="text-white text-xs opacity-30">⛶</span>
              </div>
            </div>
            <p className="text-xs text-slate-500 mt-3">
              المستطيلات المتقطعة هي المناطق المحمية، والخط الأصفر المتقطع خط إرشادي للتوضيح فقط ولا يؤثر على الضغط.
              المربع البنفسجي هو منطقة قائمة الإعدادات — مفتوحة دائماً ومقصوصة من أي حماية،
              فقائمة ⚙️ والجودة تشتغل في الوضع العادي وأثناء المهلة.
              {cfg.rtlMirror
                ? ' وضع العكس مفعّل: كل حاجة معكوسة للشمال (يوتيوب عربي) بنفس المقاسات.'
                : ' لو يوتيوب عند الطلبة بالعربي (الأزرار شمال)، فعّل «عكس الاتجاه» فوق.'}
              الشريط الأخضر أسفل المعاينة محجوز لشريط تحكم يوتيوب — الحماية لا تدخله أبداً.
              زر «معاينة وضع المهلة» يريك ما سيحدث أثناء المهلة: المستطيل السماوي وحده المفتوح للتحكم وباقي الفيديو محجوب.
            </p>
          </div>

          {/* ── Controls ── */}
          <div className="glass-panel p-6 space-y-5">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!cfg.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
                className="w-5 h-5 accent-red-500"
              />
              <span className="font-bold">تفعيل طبقة الحماية</span>
            </label>

            <label className="flex items-center gap-3 cursor-pointer rounded-xl border border-amber-400/30 bg-amber-400/5 px-4 py-3">
              <input
                type="checkbox"
                checked={!!cfg.rtlMirror}
                onChange={(e) => set('rtlMirror', e.target.checked)}
                className="w-5 h-5 accent-amber-500"
              />
              <span className="font-bold">
                عكس الاتجاه لليوتيوب العربي (RTL)
                <span className="block text-xs font-normal text-slate-400 mt-0.5">
                  يوتيوب بالعربي بيعكس أزراره للشمال — فعّل ده عشان الحماية والمربع البنفسجي وزرار ⛶ والخط يروحوا الشمال بنفس المقاسات
                </span>
              </span>
            </label>

            {/* ── Mode selector ── */}
            <div>
              <p className="text-sm font-bold text-slate-300 mb-2">شكل الحماية</p>
              <div className="grid grid-cols-3 gap-2">
                {modes.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => set('mode', m.id)}
                    className={`flex items-center justify-center gap-2 px-3 py-3 rounded-xl font-bold text-sm border transition ${
                      cfg.mode === m.id
                        ? 'bg-red-600 border-red-500 text-white shadow-lg'
                        : 'border-white/15 text-slate-300 hover:bg-white/5'
                    }`}
                  >
                    <m.Icon size={18} />
                    {m.label}
                  </button>
                ))}
              </div>
            </div>

            {(!cfg.mode || cfg.mode === 'single') && (
              <div className="grid md:grid-cols-2 gap-5">
                {sliderRow('الموضع الأفقي — من اليسار', 'x', 'أين تبدأ المنطقة المحمية أفقياً')}
                {sliderRow('الموضع الرأسي — من الأعلى', 'y', 'أين تبدأ المنطقة المحمية رأسياً')}
                {sliderRow('عرض المنطقة', 'w')}
                {sliderRow('ارتفاع المنطقة', 'h')}
              </div>
            )}

            {cfg.mode === 'vsplit' && (
              <div className="space-y-5">
                {sliderRow('مكان الخط الرأسي — من اليسار', 'split', 'الخط الفاصل بين المنطقتين (لليمين/للشمال)')}
                <div className="grid md:grid-cols-2 gap-5">
                  {sliderRow('البداية الرأسية — شمال الخط', 'yA', 'من الأعلى: أين يبدأ النصف الشمال')}
                  {sliderRow('ارتفاع الحماية — شمال الخط', 'sizeA', 'مثال: 100 = حماية كاملة في هذا الجزء')}
                  {sliderRow('البداية الرأسية — يمين الخط', 'yB', 'من الأعلى: أين يبدأ النصف اليمين')}
                  {sliderRow('ارتفاع الحماية — يمين الخط', 'sizeB', 'مثال: 50 = حماية لنصف هذا الجزء فقط')}
                </div>
              </div>
            )}

            {cfg.mode === 'hsplit' && (
              <div className="space-y-5">
                {sliderRow('مكان الخط الأفقي — من الأعلى', 'split', 'الخط الفاصل بين المنطقتين (لفوق/لتحت)')}
                <div className="grid md:grid-cols-2 gap-5">
                  {sliderRow('البداية الأفقية — فوق الخط', 'xA', 'من اليسار: أين يبدأ النصف العلوي')}
                  {sliderRow('عرض الحماية — فوق الخط', 'sizeA', 'مثال: 100 = حماية كاملة في هذا الجزء')}
                  {sliderRow('البداية الأفقية — تحت الخط', 'xB', 'من اليسار: أين يبدأ النصف السفلي')}
                  {sliderRow('عرض الحماية — تحت الخط', 'sizeB', 'مثال: 50 = حماية لنصف هذا الجزء فقط')}
                </div>
              </div>
            )}

            {sliderRow('درجة تعتيم طبقة الحماية', 'opacity', '0 = شفافة تماماً، 100 = سوداء تماماً')}

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={!!cfg.allowToggle}
                onChange={(e) => set('allowToggle', e.target.checked)}
                className="w-5 h-5 accent-red-500"
              />
              <span className="font-bold flex items-center gap-2">
                السماح للطالب بتشغيل / إيقاف الفيديو عند الضغط داخل المنطقة المحمية
                {cfg.allowToggle ? <Play size={16} /> : <Pause size={16} />}
              </span>
            </label>
            <p className="text-xs text-slate-500">
              عند إيقاف هذا الخيار: الضغط داخل المنطقة المحمية لا يفعل شيئاً إطلاقاً (تمنع التشغيل والإيقاف معاً).
            </p>

            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={cfg.graceEnabled !== false}
                onChange={(e) => set('graceEnabled', e.target.checked)}
                className="w-5 h-5 accent-red-500"
              />
              <span className="font-bold flex items-center gap-2">
                مهلة اختيار الجودة: ضغطة الطالب على الحتة الفاضية توقف الحماية ثواني مع عدّاد تنازلي
                <span>⏳</span>
              </span>
            </label>
            {cfg.graceEnabled !== false && (
              <>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-bold text-slate-300">مدة المهلة بالثواني</label>
                    <span className="text-sm font-black text-brand-gold" dir="ltr">
                      {Math.min(60, Math.max(3, Number(cfg.graceSeconds) || 10))}s
                    </span>
                  </div>
                  <input
                    type="range"
                    min={3}
                    max={30}
                    value={Math.min(30, Math.max(3, Number(cfg.graceSeconds) || 10))}
                    onChange={(e) => set('graceSeconds', Number(e.target.value))}
                    className="w-full accent-red-500"
                    dir="ltr"
                  />
                  <p className="text-xs text-slate-500 mt-1">من 3 إلى 30 ثانية — يختار فيهم الطالب الجودة ثم ترجع الحماية لوحدها.</p>
                </div>

                <div className="rounded-xl border border-cyan-400/30 bg-cyan-400/5 p-4 space-y-4">
                  <p className="text-sm font-bold text-cyan-300">المنطقة المسموحة أثناء المهلة (المستطيل السماوي في المعاينة)</p>
                  <div className="grid md:grid-cols-2 gap-5">
                    {sliderRow('الموضع الأفقي — من اليسار', 'graceX')}
                    {sliderRow('الموضع الرأسي — من الأعلى', 'graceY')}
                    {sliderRow('العرض', 'graceW')}
                    {sliderRow('الارتفاع', 'graceH')}
                  </div>
                  <p className="text-xs text-slate-500">أثناء المهلة: هذا المستطيل وحده المفتوح للتحكم (اختيار الجودة)، وباقي الفيديو محجوب تماماً.</p>
                </div>

                <label className="flex items-center gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={cfg.showTimer !== false}
                    onChange={(e) => set('showTimer', e.target.checked)}
                    className="w-5 h-5 accent-red-500"
                  />
                  <span className="font-bold">إظهار عدّاد المهلة للطالب (⏳)</span>
                </label>
              </>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-sm font-bold text-slate-300">حجم كلمة «سبيل التفوق» تحت الفيديو</label>
                <span className="text-sm font-black text-brand-gold" dir="ltr">
                  {Math.min(26, Math.max(10, Number(cfg.brandSize) || 14))}px
                </span>
              </div>
              <input
                type="range"
                min={10}
                max={22}
                value={Math.min(22, Math.max(10, Number(cfg.brandSize) || 14))}
                onChange={(e) => set('brandSize', Number(e.target.value))}
                className="w-full accent-red-500"
                dir="ltr"
              />
              <p className="text-xs text-slate-500 mt-1">من 10 إلى 22 بكسل.</p>
              <div className="grid md:grid-cols-2 gap-5">
                {sliderRow('عرض الشريط (%)', 'brandW')}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-bold text-slate-300">طول الشريط (بكسل)</label>
                    <span className="text-sm font-black text-brand-gold" dir="ltr">
                      {Math.min(64, Math.max(24, Number(cfg.brandH) || 34))}px
                    </span>
                  </div>
                  <input
                    type="range"
                    min={24}
                    max={64}
                    value={Math.min(64, Math.max(24, Number(cfg.brandH) || 34))}
                    onChange={(e) => set('brandH', Number(e.target.value))}
                    className="w-full accent-red-500"
                    dir="ltr"
                  />
                  <p className="text-xs text-slate-500 mt-1">من 24 إلى 64 بكسل.</p>
                </div>
              </div>
              <div
                className="mt-2 bg-white text-black font-extrabold text-center rounded-lg py-1.5"
                style={{
                  fontSize: `${Math.min(22, Math.max(10, Number(cfg.brandSize) || 14))}px`,
                  width: `${Math.min(100, Math.max(20, Number(cfg.brandW) || 100))}%`,
                  margin: '8px auto 0',
                }}
              >
                سبيل التفوق
              </div>
            </div>

            <div className="rounded-xl border border-amber-400/30 bg-amber-400/5 p-4 space-y-4">
              <p className="text-sm font-bold text-amber-300">مقاس الشريط في ملء الشاشة (للموبايل بالعرض)</p>
              <div className="grid md:grid-cols-3 gap-5">
                {sliderRow('عرض الشريط (%)', 'brandFsW')}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-bold text-slate-300">طول الشريط (بكسل)</label>
                    <span className="text-sm font-black text-brand-gold" dir="ltr">
                      {Math.min(96, Math.max(24, Number(cfg.brandFsH) || 48))}px
                    </span>
                  </div>
                  <input
                    type="range"
                    min={24}
                    max={96}
                    value={Math.min(96, Math.max(24, Number(cfg.brandFsH) || 48))}
                    onChange={(e) => set('brandFsH', Number(e.target.value))}
                    className="w-full accent-red-500"
                    dir="ltr"
                  />
                  <p className="text-xs text-slate-500 mt-1">من 24 إلى 96 بكسل.</p>
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-bold text-slate-300">حجم الخط (بكسل)</label>
                    <span className="text-sm font-black text-brand-gold" dir="ltr">
                      {Math.min(30, Math.max(10, Number(cfg.brandFsSize) || 18))}px
                    </span>
                  </div>
                  <input
                    type="range"
                    min={10}
                    max={30}
                    value={Math.min(30, Math.max(10, Number(cfg.brandFsSize) || 18))}
                    onChange={(e) => set('brandFsSize', Number(e.target.value))}
                    className="w-full accent-red-500"
                    dir="ltr"
                  />
                  <p className="text-xs text-slate-500 mt-1">من 10 إلى 30 بكسل.</p>
                </div>
              </div>
              <div
                className="mt-2 bg-white text-black font-extrabold text-center rounded-lg py-1.5"
                style={{
                  fontSize: `${Math.min(30, Math.max(10, Number(cfg.brandFsSize) || 18))}px`,
                  width: `${Math.min(100, Math.max(20, Number(cfg.brandFsW) || 100))}%`,
                  margin: '8px auto 0',
                }}
              >
                سبيل التفوق
              </div>
              <p className="text-xs text-slate-500">
                في الفل سكرين يوتيوب بيخفي شريط التحكم بعد ثواني — الشريط الأبيض الكبير يفضل ظاهر دايماً،
                والضغط عليه يشغّل/يوقف. دوس على الشريط أو أي مكان فاضي في الشاشة عشان شريط يوتيوب يرجع يظهر.
              </p>
            </div>

            <div className="rounded-xl border border-purple-400/30 bg-purple-400/5 p-4 space-y-4">
              <p className="text-sm font-bold text-purple-300">منطقة قائمة الإعدادات (المربع البنفسجي في المعاينة)</p>
              <div className="grid md:grid-cols-2 gap-5">
                {sliderRow('الموضع الأفقي — من اليسار', 'menuX')}
                {sliderRow('العرض', 'menuW')}
                {sliderRow('الارتفاع', 'menuH')}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-bold text-slate-300">الموضع الرأسي — من الأعلى</label>
                    <span className="text-sm font-black text-brand-gold" dir="ltr">
                      {cfg.menuY === null || cfg.menuY === undefined || cfg.menuY === '' ? 'تلقائي' : `${cfg.menuY}%`}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={0}
                    max={100}
                    value={cfg.menuY === null || cfg.menuY === undefined || cfg.menuY === '' ? Math.round(Math.max(0, 100 - stripPct - (Number(cfg.menuH) || 55))) : Number(cfg.menuY)}
                    onChange={(e) => set('menuY', Number(e.target.value))}
                    className="w-full accent-red-500"
                    dir="ltr"
                  />
                  <button
                    type="button"
                    onClick={() => set('menuY', null)}
                    className="mt-1 text-xs font-bold text-purple-300 hover:text-purple-100 underline underline-offset-4"
                  >
                    إعادة للتثبيت التلقائي فوق الشريط
                  </button>
                </div>
              </div>
              <p className="text-xs text-slate-500">
                يوتيوب بيفتح قوائم ⚙️ والجودة فوق شريط التحكم — سيبه على «تلقائي» عشان يفضل لازق فوق الشريط،
                أو حرّكه رأسياً بنفسك (عمره ما يدخل منطقة الشريط). المربع مفتوح دائماً ومقصوص من أي حماية.
              </p>
            </div>

            <div className="rounded-xl border border-white/15 bg-white/5 p-4 space-y-4">
              <p className="text-sm font-bold text-slate-200">مكان وحجم زر الفل سكرين ⛶ (فوق زرار يوتيوب الأصلي)</p>
              <div className="grid md:grid-cols-2 gap-5">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-bold text-slate-300">من اليمين (بكسل)</label>
                    <span className="text-sm font-black text-brand-gold" dir="ltr">
                      {Math.min(120, Math.max(0, Number(cfg.fsBtnRight ?? 4)))}px
                    </span>
                  </div>
                  <input
                    type="range" min={0} max={120}
                    value={Math.min(120, Math.max(0, Number(cfg.fsBtnRight ?? 4)))}
                    onChange={(e) => set('fsBtnRight', Number(e.target.value))}
                    className="w-full accent-red-500" dir="ltr"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-bold text-slate-300">من فوق الشريط الأبيض (بكسل)</label>
                    <span className="text-sm font-black text-brand-gold" dir="ltr">
                      {Math.min(120, Math.max(0, Number(cfg.fsBtnBottom ?? 4)))}px
                    </span>
                  </div>
                  <input
                    type="range" min={0} max={120}
                    value={Math.min(120, Math.max(0, Number(cfg.fsBtnBottom ?? 4)))}
                    onChange={(e) => set('fsBtnBottom', Number(e.target.value))}
                    className="w-full accent-red-500" dir="ltr"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-bold text-slate-300">العرض (بكسل)</label>
                    <span className="text-sm font-black text-brand-gold" dir="ltr">
                      {Math.min(80, Math.max(24, Number(cfg.fsBtnW) || 40))}px
                    </span>
                  </div>
                  <input
                    type="range" min={24} max={80}
                    value={Math.min(80, Math.max(24, Number(cfg.fsBtnW) || 40))}
                    onChange={(e) => set('fsBtnW', Number(e.target.value))}
                    className="w-full accent-red-500" dir="ltr"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="text-sm font-bold text-slate-300">الارتفاع (بكسل)</label>
                    <span className="text-sm font-black text-brand-gold" dir="ltr">
                      {Math.min(80, Math.max(24, Number(cfg.fsBtnH) || 40))}px
                    </span>
                  </div>
                  <input
                    type="range" min={24} max={80}
                    value={Math.min(80, Math.max(24, Number(cfg.fsBtnH) || 40))}
                    onChange={(e) => set('fsBtnH', Number(e.target.value))}
                    className="w-full accent-red-500" dir="ltr"
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                بالبكسل عشان زرار يوتيوب مقاسه ثابت — حرّك الزر في المعاينة لحد ما يغطي زرار يوتيوب الأصلي بالظبط.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn-main px-8 py-3 rounded-xl font-bold hover:scale-105 transition disabled:opacity-50 flex items-center gap-2"
              >
                <Save size={18} />
                {saving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
              </button>
              <button
                type="button"
                onClick={() => { setCfg(DEFAULTS); setMsg({ type: '', text: '' }); }}
                className="px-6 py-3 rounded-xl font-bold border border-white/20 hover:bg-white/10 transition flex items-center gap-2"
              >
                <RotateCcw size={18} />
                استعادة الافتراضي
              </button>
            </div>

            {msg.text && (
              <p className={`text-sm font-bold ${msg.type === 'ok' ? 'text-green-400' : 'text-red-400'}`}>
                {msg.text}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  );
};
